import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuList } from "@/modern/components/swivu-list";
import { TAGS_KEYS } from "@/other/constants/TagsKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useState, useEffect, useMemo } from "react";
import { TagValue } from "@/bryntum-lib/models/TagValue";
import { toast } from "sonner";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { STORE_KEYS } from "@/other/constants/StoreKeys";

const TaskCategoryDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user } = useSwivuBryntumContext();
  const [categories, setCategories] = useState<TagValue[]>([]);

  const canEdit = useMemo<boolean>(() => {
    if (!user) return false;
    return user.can(GRANTS.grant_one);
  }, [user])

  const { subscribe } = useSwivuStore({
    storeId: STORE_KEYS.taskTagValuesStore,
  })

  const loadCategories = () => {
    if (!project) return;
    const tagValueStore = project.taskTagValues;
    const categoryRecords = tagValueStore.query((record: TagValue) => record.tagSet === TAGS_KEYS.category, true) || [];
    setCategories(() => categoryRecords);
  }

  useEffect(() => {
    loadCategories();
    return subscribe(() => {
      loadCategories();
    })
  }, [subscribe]);

  const isDuplicateCategoryName = (name: string, excludeCategoryId?: string | number) => {
    if (!project) return false;
    const tagValueStore = project.taskTagValues;
    const allCategories = tagValueStore.query((record: TagValue) => record.tagSet === TAGS_KEYS.category, true) || [];
    return allCategories.some(c => {
      if (excludeCategoryId !== undefined && c.id === excludeCategoryId) {
        return false;
      }
      return c.tagValue.toLowerCase() === name.toLowerCase();
    });
  };

  const handleAddItem = (value: string) => {
    if (!value.trim()) {
      return;
    }

    if (isDuplicateCategoryName(value)) {
      toast.error('Category name already exists');
      return;
    }

    if (!project) return;
    const tagValueStore = project.taskTagValues;
    const newCategory = tagValueStore.createRecord({
      tagSet: TAGS_KEYS.category,
      tagValue: value,
    });
    tagValueStore.add(newCategory);
  }

  const handleReorderItems = (movedItem: TagValue, beforeItem: TagValue | null) => {
    if (!project) return;
    const tagValueStore = project.taskTagValues;
    // @ts-expect-error - tagValue is a TagValue model
    tagValueStore.move(movedItem, beforeItem);
  }

  const handleUpdateCategoryName = (category: TagValue, newName: string) => {
    if (!newName.trim()) {
      return false;
    }

    if (isDuplicateCategoryName(newName, category.id)) {
      toast.error('Category name already exists');
      return false;
    }

    category.tagValue = newName;
    return true;
  }

  const handleDeleteCategory = (category: TagValue) => {
    if (!project) return;
    const tagValueStore = project.taskTagValues;
    // @ts-expect-error - category is a TagValue model
    tagValueStore.remove(category);
  }

  return (
    <div className="p-6 flex flex-col max-w-3xl w-full h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <SwivuList.Root<TagValue>
        items={categories}
        onReorder={handleReorderItems}
        searchConfig={{
          fields: ["tagValue"],
          placeholder: "Search categories...",
        }}
      >
        <SwivuList.Toolbar />

        <SwivuList.Content<TagValue> emptyMessage="No categories yet. Create your first category!">
          {(category: TagValue) => (
            <SwivuList.Item value={category.id as string | number}>
              {/* Drag Handle */}
              <SwivuList.ItemHandle disabled={!canEdit} value={category.id as string | number} />

              {/* Category Name */}
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <SwivuList.TextField
                  item={category}
                  value={category.tagValue}
                  onUpdate={handleUpdateCategoryName}
                  placeholder="Enter category name"
                  disabled={!canEdit}
                  className="flex-1"
                />
              </div>

              {/* Actions */}
              <SwivuList.ItemActions
                disabled={!canEdit}
                onDelete={() => handleDeleteCategory(category)}
              />
            </SwivuList.Item>
          )}
        </SwivuList.Content>

        <SwivuList.QuickAdd
          disabled={!canEdit}
          onAdd={handleAddItem}
          placeholder="Add new category..."
        />
      </SwivuList.Root>
    </div>
  );
};

export default TaskCategoryDetail;
